import { useEffect, useState } from "react";
import { Button } from "@tremor/react";
import * as profileAPI from "../../../services/profiles";
import SourcePicker from "./SourcePicker";
import { CUSTOM, LORA, isValid, normalizeDevEui, validateDetails } from "./wizard";

const inputClass = "mt-2 w-full rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3 text-gray-900 dark:text-gray-50 dark:bg-gray-900 focus:border-blue-600 focus:outline-none";

export default function DetailsStep({ state, modelLabel, onChange, onChangeModel, onBack, onSubmit, submitting, error }) {
  const lora = state.protocol === LORA;
  const errors = validateDetails(state);
  const [profiles, setProfiles] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!lora || state.model !== CUSTOM) return;
    profileAPI.list(1, 100).then((r) => setProfiles((r && r.data) || [])).catch(() => setProfiles([]));
  }, [lora, state.model]);

  const onProfile = (profileId) => {
    const profile = profiles.find((p) => p._id === profileId);
    const source = profile && profile.source;
    const sourceId = source && typeof source === "object" ? source._id : source;
    onChange(state.sourceId || !sourceId ? { profileId } : { profileId, sourceId });
  };

  const customProfiles = profiles.filter((p) => !p.catalogModel);

  const serialLabel = lora ? "DevEUI" : "Serial";
  const serialValid = !errors.serial && state.serial.length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid(state)) {
      setShowErrors(true);
      return;
    }
    onSubmit();
  };

  return (
    <section>
      <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">Device details</h2>

      {lora && (
        <div className="mt-6 flex items-center justify-between rounded-xl bg-gray-100 dark:bg-gray-800 px-5 py-4">
          <span className="text-gray-600 dark:text-gray-400">Device <b className="text-gray-900 dark:text-gray-50">{modelLabel}</b></span>
          <button type="button" onClick={onChangeModel} className="text-blue-700 dark:text-blue-400">Change</button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mt-6 space-y-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
          <div>
            <label htmlFor="device-name" className="font-medium text-gray-900 dark:text-gray-50">Name</label>
            <input
              id="device-name"
              className={inputClass}
              value={state.name}
              onChange={(e) => onChange({ name: e.target.value })}
            />
            <p className={`mt-2 text-sm ${errors.name ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400"}`}>
              {errors.name || "We picked a name for you."}
            </p>
          </div>

          <div>
            <div className="flex justify-between">
              <label htmlFor="device-serial" className="font-medium text-gray-900 dark:text-gray-50">{serialLabel}</label>
              {lora && (
                <span className={`font-mono text-sm ${serialValid ? "text-green-700 dark:text-green-400" : "text-gray-500 dark:text-gray-400"}`}>
                  {state.serial.length} / 16
                </span>
              )}
            </div>
            <input
              id="device-serial"
              className={`${inputClass} font-mono ${serialValid ? "border-green-600" : ""}`}
              value={state.serial}
              placeholder={lora ? "A840 4100 0181 C061" : ""}
              onChange={(e) => onChange({ serial: normalizeDevEui(e.target.value) })}
            />
            <p className={`mt-2 text-sm ${serialValid ? "text-green-700 dark:text-green-400" : errors.serial && state.serial ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400"}`}>
              {serialValid
                ? "Looks right."
                : errors.serial && state.serial
                  ? errors.serial
                  : lora
                    ? "Printed on the device label or box. Spaces and colons are fine."
                    : "We generated a serial for you. 6 to 16 letters or digits."}
            </p>
          </div>

          {lora && state.model === CUSTOM && (
            <div>
              <label htmlFor="device-profile" className="font-medium text-gray-900 dark:text-gray-50">Profile</label>
              <select
                id="device-profile"
                className={inputClass}
                value={state.profileId}
                onChange={(e) => onProfile(e.target.value)}
              >
                <option value="">Select a profile</option>
                {customProfiles.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
              {showErrors && errors.profile && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.profile}</p>
              )}
            </div>
          )}

          {lora && (
            <div>
              <SourcePicker value={state.sourceId} onChange={(sourceId) => onChange({ sourceId })} />
              {showErrors && errors.source && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.source}</p>
              )}
            </div>
          )}
        </div>

        {error && (
          <div role="alert" className="mt-4 rounded-lg bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300">{error}</div>
        )}

        <div className="mt-6 flex justify-between">
          <Button type="button" variant="secondary" onClick={onBack}>Back</Button>
          <Button type="submit" disabled={!isValid(state) || submitting} loading={submitting}>
            Add device
          </Button>
        </div>
      </form>
    </section>
  );
}
